import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isTransientQueryError } from "@sx/api-client";
import { QueryErrorState, useClock } from "@sx/ui";
import {
  Outlet,
  createFileRoute,
  useParams,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";

import { accessibleProjectIds, canAccessWorkspace } from "../lib/access";
import type { Project } from "../lib/account";
import {
  matchConsoleArea,
  projectScopedPath,
  type ProjectAddress,
  type WorkspaceId,
} from "../lib/nav";
import { started } from "../lib/prefetch";
import { consoleQueryCache } from "../lib/query-cache";
import { keepClockSetting } from "../lib/clock-setting";
import { BrowserStateProvider } from "../lib/browser-state";
import { ConsoleFavourites } from "../lib/favourites";
import { DurableUploadProvider } from "../lib/durable-upload-manager";
import { posthog } from "../posthog";
import { useLogout, useMe, type Session } from "../lib/session";
import { ConsoleShell } from "../shell/ConsoleShell";
import { WorkspaceAccessDenied } from "../shell/WorkspaceGate";
import { NotFound } from "../shell/NotFound";
import {
  ProjectProvider,
  ProjectAccessUnavailable,
  projectAddress,
  useProject,
  type ProjectScope,
} from "../shell/project";
import { RedirectToLogin } from "../shell/RedirectToLogin";
import {
  featureEnabled,
  featureAccessible,
  useOrganizationFeatures,
  useProductionContext,
} from "../lib/organization-features";
import { Region } from "../components/Region";
import { OrganizationFeatureGate } from "../shell/OrganizationFeatureGate";
import { COLD_LOAD_WAIT, RouteFallback } from "../shell/RouteFallback";
import {
  CONSOLE_WORKSPACES,
  GROUP_ORGANIZATION,
  GROUP_PROJECT,
} from "../platform.gen";

/* Two waits, not four.
 *
 * Every signed-in address needs the same four things before its page can ask its own
 * question: who you are, what you own, which Project you are in, and what this workspace
 * grants you. Each of those used to be a `useQuery` inside a component that only mounted
 * once the one above it had answered, so opening a workspace was four sequential network
 * phases with a fallback in front of each. They are not four phases; they are one wait
 * and three reads started beside it.
 *
 * `beforeLoad` is the wait and holds exactly one read — identity, because every other key
 * is scoped to it, and because the shell's frame needs nothing more. The `loader` below
 * starts the portfolio and returns: the router runs every matched route's loader at once,
 * so the portfolio, a page's own reads and a workspace's grant all start together, and
 * each part of the screen that needs one waits for it in its own slot.
 *
 * Nothing here throws. A door that is down must not take the shell with it (standing rule
 * 13: every address survives one of its doors being down), so a failed read is left in the
 * cache as a failure and the component that owns it — the session gate here, a workspace
 * gate below, a region on a page — is what says so, with a retry. The loader's only job is
 * to have *started* the read. */
// The clock is restored before the first page reads it, so a reload never remounts.
keepClockSetting();

export const Route = createFileRoute("/_console")({
  beforeLoad: async ({ context }) => {
    // Identity is never persisted, so this is always a door call, and it is the answer
    // that decides whether anything restored from the last reload belongs to the person
    // at the keyboard. `own` drops the whole snapshot when it does not — a device is
    // shared, and a global or project-keyed read carries no principal to filter on.
    // This route's component also uses the session module. Loading the route-side
    // query explicitly keeps that component import in TanStack's lazy half instead
    // of pulling all of its transitive door clients into the first-paint graph.
    const { sessionQuery } = await import("../lib/session");
    // One try here: a door that is restarting must not hold the page. The gate below
    // keeps asking, with backoff, behind a quiet "Reconnecting…".
    const session = await started(
      context.queryClient.ensureQueryData({ ...sessionQuery, retry: false }),
    );
    if (
      consoleQueryCache().own(session?.principal.id) &&
      session !== undefined
    ) {
      // Clearing took the identity we just confirmed with it. Put it back rather than
      // paying for a second round trip to learn the same thing.
      context.queryClient.setQueryData(sessionQuery.queryKey, session);
    }
  },
  // Started, not awaited: the shell draws from identity alone, and what the portfolio
  // decides — the project switcher, the rail's workspaces, the page — fills its own slot.
  loader: async ({ context }) => {
    const [
      { loadedSession },
      { organizationsQuery, projectsQuery, organizationFeaturesQuery },
    ] = await Promise.all([import("../lib/session"), import("../lib/account")]);
    const principalId = loadedSession(context.queryClient)?.principal.id;
    if (principalId === undefined) return;
    void started(
      context.queryClient.ensureQueryData(organizationsQuery(principalId)),
    );
    void started(context.queryClient.ensureQueryData(projectsQuery(principalId)));
    void started(
      context.queryClient.ensureQueryData(
        organizationFeaturesQuery(principalId),
      ),
    );
  },
  // The one wait the router draws. Every other route keeps the old page on screen while it
  // loads (`router.tsx`); a cold load has no old page, so the shell's own frame — the rail
  // and the bar at their final sizes — paints at once and the console fills in beneath it.
  ...COLD_LOAD_WAIT,
  component: ConsoleLayout,
});

/* Everything the browser retains for one signed-in person.
 *
 * `DurableUploadProvider` is composed here rather than inside `BrowserStateProvider`
 * because the two modules sit on opposite sides of the first-paint line.
 * `lib/browser-state.tsx` is eager — `usePrincipalId` is read from route halves the
 * entry always loads — while the upload manager drives the Catalog and Worlds door
 * clients, so importing it from there put the whole generated RPC runtime into
 * `index.html`'s modulepreload closure (420 KB gz against a 180 KB budget). This route's
 * component is TanStack's lazy half, the same reason its `beforeLoad` above imports
 * `lib/session` dynamically, so the manager arrives with the console rather than before
 * it. The mount point in the rendered tree is unchanged: still under the principal
 * context and the turn outbox, still above every workspace, so a transfer survives
 * navigation exactly as before. */
function ConsoleBrowserState({
  principalId,
  children,
}: {
  readonly principalId: string;
  readonly children: ReactNode;
}) {
  return (
    <BrowserStateProvider principalId={principalId}>
      <DurableUploadProvider principalId={principalId}>
        <ConsoleFavourites principalId={principalId}>{children}</ConsoleFavourites>
      </DurableUploadProvider>
    </BrowserStateProvider>
  );
}

/** The workspace an area names, when the address is inside one. */
function workspaceOf(area: ReturnType<typeof matchConsoleArea>) {
  return CONSOLE_WORKSPACES.find((workspace) => workspace.id === area);
}

/** Where the launcher and the switcher send a project-scoped destination. */
const PROJECT_HOME = CONSOLE_WORKSPACES.find(
  (workspace) => workspace.id === "projects",
)!;

function organizationIdFromPath(pathname: string): string | undefined {
  const match = /^\/settings\/organizations\/([^/]+)$/.exec(pathname);
  return match?.[1] === "new" ? undefined : match?.[1];
}

/* The one session gate. It sits here, above every workspace, because there is now exactly
 * one identity to establish: one origin, one cookie, one `/d/auth/api/auth/session` call.
 * Below this boundary a workspace may still be told 403 by its own door — that is a grant
 * question, and the workspace answers it — but nothing below re-asks who the user is.
 *
 * Deliberately NOT under this layout: the capture kiosk display, which runs unattended on
 * a pod screen with no session and no chrome, and so lives at the top level. */
function ConsoleLayout() {
  const me = useMe();
  const logout = useLogout(me.data?.principal.id);
  const queryClient = useQueryClient();
  const previousPrincipal = useRef<string | undefined>(undefined);
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const router = useRouter();
  // The two slugs, straight off whichever route matched. `strict: false` because this
  // layout sits above both the addresses that carry a project and the ones that do not,
  // and "no project in this address" is the honest answer for the second kind.
  const { org, project } = useParams({ strict: false });
  const address =
    org === undefined || project === undefined ? undefined : { org, project };
  const area = matchConsoleArea(pathname);

  /* Choosing a project IS navigation — there is nowhere else for the choice to live.
   *
   * It lands on the same workspace under the new project, or on that project's home when
   * the current address carries no project at all (the switcher is reachable from Capture
   * and Settings too). It deliberately does not try to carry the rest of the path across:
   * a task id, a run id or a dataset name belongs to the project that was left. */
  const selectProject = useCallback(
    (next: Project) => {
      const workspace = workspaceOf(
        matchConsoleArea(router.state.location.pathname),
      );
      void router.navigate({
        href: projectScopedPath(
          { org: next.organizationSlug, project: next.slug },
          workspace?.projectScoped === true ? workspace : PROJECT_HOME,
        ),
      });
    },
    [router],
  );

  useEffect(() => {
    const current = me.data?.principal.id;
    if (
      previousPrincipal.current !== undefined &&
      previousPrincipal.current !== current
    ) {
      queryClient.clear();
    }
    previousPrincipal.current = current;
  }, [me.data?.principal.id, queryClient]);

  // No pending branch: the route's `beforeLoad` above already waited for this read, and
  // the route's own `pendingComponent` is the one place that wait is drawn. What is left
  // here are the three answers — failed, signed out, signed in.
  // A door that is briefly unreachable (a restart) is not an answer about the session:
  // keep the console if we already know who this is, else wait quietly while it retries.
  if (me.isError && me.data === undefined && isTransientQueryError(me.error)) {
    return (
      <div className="grid h-full place-items-center p-6">
        <p role="status" className="text-supporting text-muted-foreground">
          Reconnecting…
        </p>
      </div>
    );
  }
  if (me.isError && me.data === undefined) {
    return (
      <div className="grid h-full place-items-center p-6">
        <QueryErrorState
          title="Sign-in status could not be checked"
          error={me.error}
          retry={() => void me.refetch()}
        />
      </div>
    );
  }
  if (me.data === null) return <RedirectToLogin />;
  if (me.data === undefined) return <RouteFallback />;
  // Whoever the door says this session belongs to — including while staff are
  // acting as someone else, because then it is that person's session and the whole
  // shell is theirs. There is nothing to project on top of it.
  const session = me.data;

  /* The URL was parsed into an area once, above. An address under `_console` that names no
   * area is not an area with no nav — it is either an address from before the rename or a
   * wrong one, and the catch-all below is the one place that can tell them apart. It needs
   * the portfolio to do it (an old `<uuid>` becomes a slug pair only against the projects
   * this identity holds), so it gets the project context — and no shell, because there is
   * no workspace to draw one for. */
  if (area === undefined) {
    return (
      <ConsoleBrowserState principalId={me.data.principal.id}>
        <ProjectProvider address={address} onSelect={selectProject}>
          <Outlet />
        </ProjectProvider>
      </ConsoleBrowserState>
    );
  }

  /* Choosing an organization is navigation too. Here it is its own settings page; where the
   * portfolio is known, below, it opens one of its projects unless the person is in Settings
   * or it holds no enabled project. */
  const onOrganizationSelect = (next: string) => {
    void router.navigate({
      href: `/settings/organizations/${encodeURIComponent(next)}`,
    });
  };
  const contentProps = {
    area,
    session,
    analyticsPrincipal: me.data.principal,
    onSignOut: () => void logout.mutate(),
    signingOut: logout.isPending,
    organizationId: organizationIdFromPath(pathname),
    onOrganizationSelect,
  };
  // Every admitted human resolves to at least one grant-bound Project. Project state spans
  // every console destination; opening the Project does not widen the pillar roles already
  // carried by the session.
  const hasProjectContext =
    session.principal.memberships.length > 0 ||
    session.project_audiences.length > 0 ||
    address !== undefined;
  if (!hasProjectContext) {
    return (
      <ConsoleBrowserState principalId={me.data.principal.id}>
        <ConsoleContent
          {...contentProps}
          projectId={undefined}
          workspaceAddress={() => undefined}
          onProjectSelect={() => {}}
        />
      </ConsoleBrowserState>
    );
  }
  return (
    <ConsoleBrowserState principalId={me.data.principal.id}>
      <ProjectProvider
        address={address}
        accessibleProjectIds={
          area === "settings" ? undefined : accessibleProjectIds(session, area)
        }
        onSelect={selectProject}
      >
        <ProjectContextContent {...contentProps} />
      </ProjectProvider>
    </ConsoleBrowserState>
  );
}

interface ConsoleContentProps {
  readonly projectScope?: ProjectScope;
  readonly area: NonNullable<ReturnType<typeof matchConsoleArea>>;
  readonly session: Session;
  readonly analyticsPrincipal: Session["principal"];
  readonly onSignOut: () => void;
  readonly signingOut: boolean;
  readonly organizationId?: string;
  readonly projectId?: string;
  readonly workspaceAddress: (
    workspace: WorkspaceId,
  ) => ProjectAddress | undefined;
  readonly onOrganizationSelect: (organizationId: string) => void;
  readonly onProjectSelect: (projectId: string) => void;
}

function ProjectContextContent(
  props: Omit<
    ConsoleContentProps,
    "projectId" | "workspaceAddress" | "onProjectSelect"
  >,
) {
  const project = useProject();
  const features = useOrganizationFeatures();
  const router = useRouter();
  // An address naming a project this identity does not hold is a wrong address, not an
  // invitation to open a different one. The portfolio has answered by the time this is
  // true, so it is a 404 rather than a race.
  if (project.unknown) return <NotFound />;
  return (
    <ConsoleContent
      {...props}
      projectId={project.selectedId}
      projectScope={project}
      workspaceAddress={(workspace) => {
        /* Which project this workspace opens in. `accessibleProjectIds` is the grant
           filter that keeps a Catalog-only demo project from making Train look absent;
           the project in hand wins whenever it holds the grant. */
        const reachable =
          features.organizations
            ?.find(
              (org) =>
                org.organizationId ===
                (props.organizationId ??
                  project.selected?.organizationId ??
                  props.session.principal.org.id),
            )
            ?.features.find((feature) => feature.workspaceId === workspace)
            ?.accessibleProjectIds ??
          accessibleProjectIds(props.session, workspace);
        const candidates = project.all.filter(
          (candidate) =>
            !candidate.disabled &&
            candidate.organizationId ===
              (props.organizationId ?? project.selected?.organizationId) &&
            (reachable === undefined || reachable.includes(candidate.id)),
        );
        const chosen =
          candidates.find((candidate) => candidate.id === project.selectedId) ??
          candidates[0] ??
          project.all.find(
            (candidate) =>
              !candidate.disabled &&
              candidate.organizationId ===
                (props.organizationId ?? project.selected?.organizationId),
          );
        return chosen === undefined ? undefined : projectAddress(chosen);
      }}
      onProjectSelect={project.select}
      onOrganizationSelect={(organizationId) => {
        const destination = project.organizationDestination(
          organizationId,
          props.area === "settings",
        );
        switch (destination.kind) {
          case "here":
            return;
          case "project":
            project.select(destination.project.id);
            return;
          case "home":
            void router.navigate({
              href: projectScopedPath(
                projectAddress(destination.project),
                PROJECT_HOME,
              ),
            });
            return;
          case "settings":
            props.onOrganizationSelect(organizationId);
        }
      }}
    />
  );
}

function ConsoleContent({
  area,
  session,
  analyticsPrincipal,
  projectScope,
  onSignOut,
  signingOut,
  organizationId,
  projectId,
  workspaceAddress,
  onOrganizationSelect,
  onProjectSelect,
}: ConsoleContentProps) {
  const clock = useClock();
  const features = useOrganizationFeatures();
  const featureOrganizationId =
    organizationId ??
    projectScope?.selected?.organizationId ??
    session.principal.memberships.find(
      (membership) => membership.organization.id === session.principal.org.id,
    )?.organization.id ??
    session.principal.memberships[0]?.organization.id;
  // Production and Robot operations' Units stand on the factory floor, outside every
  // project: both read the floor's own project and organization, never the URL's.
  const onTheFloor = (workspace: WorkspaceId | "settings") =>
    workspace === "experience-production" || workspace === "robot-fleet";
  const production = useProductionContext(
    onTheFloor(area) ||
      !!features.organizations?.some((org) =>
        org.features.some(
          (feature) =>
            feature.workspaceId === "experience-production" &&
            feature.available,
        ),
      ),
  );
  const organizationFor = (workspace: WorkspaceId) =>
    onTheFloor(workspace)
      ? production.context?.organizationId
      : featureOrganizationId;
  const enabled = (workspace: WorkspaceId) =>
    featureEnabled(
      features.organizations,
      organizationFor(workspace),
      workspace,
      onTheFloor(workspace)
        ? production.context?.projectId
        : undefined,
    ) &&
    (!onTheFloor(workspace) || production.context !== undefined);
  const accessible = (workspace: WorkspaceId, accessProjectId = projectId) =>
    featureAccessible(
      features.organizations,
      organizationFor(workspace),
      workspace,
      onTheFloor(workspace)
        ? production.context?.projectId
        : accessProjectId,
    );
  const content =
    area === "settings" ? (
      <Outlet key={clock} />
    ) : area === "projects" ? (
      canAccessWorkspace(session, area, projectId, projectScope?.selected?.managedAccessRole) ? (
        <Outlet key={clock} />
      ) : (
        <WorkspaceAccessDenied workspace={area} />
      )
    ) : onTheFloor(area) ? (
      <Region
        query={production.query}
        title="Factory setup"
        placeholder={<RouteFallback />}
      >
        {(context) => (
          <OrganizationFeatureGate
            query={features.query}
            organizationId={context.organizationId}
            projectId={context.projectId}
            workspace={area}
          >
            {!accessible(area) ? (
              <WorkspaceAccessDenied workspace={area} />
            ) : (
              <Outlet key={clock} />
            )}
          </OrganizationFeatureGate>
        )}
      </Region>
    ) : projectScope?.loading ? (
      <RouteFallback />
    ) : projectScope?.error ? (
      <ProjectAccessUnavailable />
    ) : (
      <OrganizationFeatureGate
        query={features.query}
        organizationId={featureOrganizationId}
        workspace={area}
      >
        {!accessible(area) || !canAccessWorkspace(session, area, projectId, projectScope?.selected?.managedAccessRole) ? (
          <WorkspaceAccessDenied workspace={area} />
        ) : (
          <Outlet key={clock} />
        )}
      </OrganizationFeatureGate>
    );
  return (
    <>
      <PostHogIdentity principal={analyticsPrincipal} projectId={projectId} />
      <ConsoleShell
        area={area}
        featureEnabled={enabled}
        featureAccessible={accessible}
        projectScope={projectScope}
        session={session}
        onSignOut={onSignOut}
        signingOut={signingOut}
        organizationId={organizationId}
        projectId={projectId}
        workspaceAddress={workspaceAddress}
        onOrganizationSelect={onOrganizationSelect}
        onProjectSelect={onProjectSelect}
        portfolioPending={
          projectScope?.loading === true ||
          (features.organizations === undefined && !features.query.isError)
        }
      >
        {/* Remounted on the clock, so every page reads its days and instants anew. */}
        {content}
      </ConsoleShell>
    </>
  );
}

/** Say who this is, and which organization and project their events belong to.
 *
 * The groups are what make the numbers answerable. Without them PostHog can say "412
 * captures"; with them it says "eleven of fourteen organizations captured, and these three
 * never did", which is the only version anyone acts on. The keys are the ids — the same
 * ones the doors send (`sx_service.analytics.Principal.groups`) — so a person's console
 * session and the API calls it causes land on one organization and one project rather than
 * on two populations that merely share a name.
 *
 * The project group follows the selected project, so it is set separately and re-runs when
 * someone switches: a person who moves from one project to another is the same person, and
 * their next events belong to the new one. */
function PostHogIdentity({
  principal,
  projectId,
}: {
  principal: Session["principal"];
  projectId?: string;
}) {
  const principalId = principal.id;
  const displayName = principal.display_name;
  const principalKind = principal.kind;
  const organizationId = principal.org.id;
  const organizationSlug = principal.org.slug;

  useEffect(() => {
    posthog.identify(principalId, {
      name: displayName,
      principal_kind: principalKind,
      organization_slug: organizationSlug,
    });
    posthog.group(GROUP_ORGANIZATION, organizationId);
  }, [
    displayName,
    organizationId,
    organizationSlug,
    principalId,
    principalKind,
  ]);

  useEffect(() => {
    // No project selected yet — the projects list, settings, the signed-out shell. An
    // absent group is correct there; asserting a stale one would file those page views
    // under whichever project happened to be open last.
    if (projectId === undefined) return;
    posthog.group(GROUP_PROJECT, projectId);
  }, [projectId]);

  return null;
}
